<script>
import {Link} from "@inertiajs/vue3";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";
import IconCartPlus from "@js/components/Icons/IconCartPlus.vue";
import IconHeart from "@js/components/Icons/IconHeart.vue";
import IconHeartFilled from "@js/components/Icons/IconHeartFilled.vue";
import ImageLoader from "@js/components/Loaders/ImageLoader.vue";
import IconScales from "@js/components/Icons/IconScales.vue";
import {useLikeStore} from "@js/store/LikeStore.js";
import {useCompareStore} from "@js/store/CompareStore.js";
import {useCartStore} from "@js/store/CartStore.js";
import IconCheckMark from "@js/components/Icons/IconCheckMark.vue";
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import {computed, toRef} from "vue";
import {useCatalogStore} from "@js/store/CatalogStore.js";
import IconInfo from "@js/components/Icons/IconInfo.vue";
import {useProductStore} from "@js/store/ProductStore.js";
import {ecomClick, mapProduct} from "@js/ecommerce.js";
import CartQuantityStepper from "@js/components/Sections/Cart/Components/CartQuantityStepper.vue";
import ProductBetonPriceSpecifications from "@js/components/Sections/Product/ProductBetonPriceSpecifications.vue";

export default {
    name: "CatalogProductItemGrid",
    components: {
        ProductBetonPriceSpecifications,
        CartQuantityStepper,
        IconInfo,
        OnlyMobile,
        OnlyDesktop,
        IconCheckMark,
        IconScales,
        NumberFormatComponent,
        IconCartPlus,
        IconHeart,
        IconHeartFilled,
        ImageLoader,
        Link,
    },
    props: {
        data: {type: Object, required: true},
        defaultMode: {type: String, default: "m"}, // "m" | "t" | "pc"
        listName: {type: String, default: 'Каталог'},
        position: {type: Number, default: 1},
    },
    methods: {
        trackClick() {
            try {
                ecomClick(mapProduct(this.data, {list: this.listName, position: this.position}))
            } catch (e) {
            }
        }
    },
    setup(props) {
        const likeStore = useLikeStore();
        const compareStore = useCompareStore();
        const cartStore = useCartStore();
        const catalogStore = useCatalogStore();
        const productStore = useProductStore();

        // Регистрация продукта и дефолтного режима для UI-состояния (m|t|pc)
        cartStore.registerCatalogProduct(props.data);

        const qtyProxy = computed({
            get: () => cartStore.uiOf(props.data.id).qty,
            set: (val) => cartStore.setQtyFromInput(props.data.id, val),
        });

        // Есть ли хоть какая-то цена (для показа прайс-блока)
        const hasAnyPrice = computed(() =>
            !!(props.data.price)
        );

        const canBuy = computed(
            () => props.data.available && cartStore.priceActive(props.data.id)
        );

        return {
            catalogStore,
            likeStore,
            compareStore,
            productStore,
            cartStore,
            qtyProxy,
            hasAnyPrice,
            canBuy,
        };
    },
};
</script>

<template>
    <div
        class="relative w-full rounded-2xl border border-gray-200 bg-white overflow-hidden h-full flex flex-col h-full gap-4">
        <!-- Картинка + бейджи -->
        <Link :href="route('product',data.slug)" @pointerdown="trackClick"
              class="relative w-full p-0">
            <div
                class="overflow-hidden w-full border-b border-gray-200 ">
                <ImageLoader :title="data.title"
                             :src="'/storage/products/'+data.images[0]"
                             imgClasses="w-full h-full object-cover object-bottom max-h-[200px]"/>
            </div>
            <!--            <div class="absolute left-0 top-0 flex gap-1">-->
            <!--                    <span v-if="data.is_promo"-->
            <!--                          class="rounded bg-amber-400 px-2 py-0.5 text-[11px] font-medium text-black">Акция</span>-->
            <!--                <span v-if="data.is_hit"-->
            <!--                      class="rounded bg-green-600 px-2 py-0.5 text-[11px] font-medium text-white">Хит</span>-->
            <!--            </div>-->
        </Link>

        <!-- Центр -->
        <div class="flex min-w-0 grow flex-col gap-2 py-0 p-3">
            <div class="w-min text-nowrap text-[11px] px-2 py-0.5 rounded"
                 :class="[data.available ? 'bg-emerald-600 text-white': 'bg-amber-400 text-black']">
                {{ data.available ? 'В наличии' : 'Ожидаем поставку' }}
            </div>

            <Link :href="route('product',data.slug)" @pointerdown="trackClick"
                  class="max-w-[400px] md:line-clamp-2 text-sm md:text-base font-medium leading-5 text-gray-900 hover:text-navHoverColor transition">
                {{ data.title }}
            </Link>

            <!-- Параметры -->
            <div class="mb-1 flex flex-wrap gap-2 text-xs font-medium text-gray-700">
                <span v-if="cartStore.areaSqM(data.id)" class="rounded bg-grayBg px-2 py-1">
                    Площадь: <NumberFormatComponent :number="cartStore.areaSqM(data.id)"
                                                    append_text=" кв.м"
                                                    prepend_text="≈ "/>
                </span>
                <span v-if="cartStore.weightKg(data.id)" class="rounded bg-grayBg px-2 py-1">
                    Вес: <NumberFormatComponent :number="cartStore.weightKg(data.id)"
                                                append_text=" т"
                                                prepend_text="≈ "/>
                </span>
                <span v-if="data.class" class="rounded bg-grayBg px-2 py-1">
                                Класс: {{data.class}}
                            </span>
                <span v-if="data.grade" class="rounded bg-grayBg px-2 py-1">
                                Марка: {{data.grade}}
                            </span>
                <ProductBetonPriceSpecifications :product-id="data.id"
                                                 :specs="data.beton_price_specifications ?? []"/>
            </div>

            <!-- Блок выбора единицы + активная цена -->
            <div v-if="hasAnyPrice" class="flex flex-col flex-wrap gap-2">
                <div>
                    <div class="text-sm text-gray-700">
                        <span class="font-medium text-sm">Цена: </span>
                        <NumberFormatComponent
                            :number="cartStore.priceActive(data.id)"
                            append_text=" ₽"
                            class="text-base md:text-lg font-semibold text-black"/>
                        <span class="font-medium text-sm">/{{ data.price_for_type?.short_title ?? 'шт' }}</span>
                    </div>
                </div>
            </div>
            <div v-else class="font-medium leading-tight">
                Цена договорная
            </div>
        </div>

        <!-- Правая колонка -->
        <div class="flex flex-col items-stretch mt-auto gap-3 pt-0 w-full p-3">
            <!-- Количество -->
            <div class="flex items-center gap-3" v-if="canBuy">
                <CartQuantityStepper v-model="qtyProxy"
                                     :min="cartStore.minQty(data.id)"
                                     :step="cartStore.step(data.id)"
                                     :disabled="!canBuy"
                                     size="md"
                                     width-class="w-32"
                                     @inc="cartStore.incQty(data.id)"
                                     @dec="cartStore.decQty(data.id)"/>
                <div class="text-base font-semibold text-gray-900">
                    <NumberFormatComponent :number="cartStore.totalPrice(data.id)"
                                           prepend_text="Итого: "
                                           append_text=" ₽"/>
                </div>
            </div>

            <!-- CTA -->
            <template v-if="!canBuy">
                <div class="flex flex-col gap-2">
                    <button @click.prevent="productStore.showFastBuyPopup(data.id)"
                            class="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-amber-400 text-base font-semibold text-black transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50">
                        Узнать цену
                    </button>
                </div>
            </template>
            <template v-else>
                <div class="flex gap-2">
                    <template v-if="cartStore.isInCartExact(data.id)">
                        <!-- Уже в корзине (в текущей единице) -->
                        <Link :href="route('cart')"
                              class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 text-sm font-semibold text-white transition hover:bg-emerald-500">
                            <IconCheckMark class="w-5 h-5"/>
                            Уже в корзине
                        </Link>
                    </template>
                    <template v-else>
                        <!-- Ещё нет в корзине -->
                        <button @click="cartStore.addToCartFromCard(data.id)" :disabled="!canBuy"
                                class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-baseColor text-sm font-semibold text-black md:hover:text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50">
                            <IconCartPlus class="h-5 w-5"/>
                            В корзину
                        </button>
                    </template>
                    <button @click.prevent="productStore.showFastBuyPopup(data.id)"
                            class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-black/10 border-black/10 text-sm font-semibold text-black transition hover:bg-baseColor disabled:cursor-not-allowed disabled:opacity-50">
                        Купить сейчас
                    </button>
                </div>
            </template>
        </div>

        <!-- Избранное/Сравнение -->
        <div class="absolute flex gap-2 end-2 top-2">
            <button
                class="group inline-flex h-9 w-9 shadow-sm items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50"
                @click.prevent="compareStore.toggleCompare(data.id)"
                :aria-pressed="compareStore.isCompared(data.id)"
                :title="compareStore.isCompared(data.id) ? 'Убрать из сравнения' : 'В сравнении'">
                <IconScales v-if="compareStore.isCompared(data.id)"
                            class="min-w-5 min-h-5 max-w-5 max-h-5 mb-[3px] text-blue-500"/>
                <IconScales v-else
                            class="min-w-5 min-h-5 max-w-5 max-h-5 mb-[3px] text-gray-700 group-hover:text-blue-500 transition"/>
            </button>
            <button
                class="group inline-flex h-9 w-9 shadow-sm items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50"
                @click.prevent="likeStore.toggleLike(data.id)"
                :aria-pressed="likeStore.isLiked(data.id)"
                :title="likeStore.isLiked(data.id) ? 'Убрать из избранного' : 'В избранное'">
                <IconHeartFilled v-if="likeStore.isLiked(data.id)" class="h-5 w-5 text-likeColor"/>
                <IconHeart v-else class="h-5 w-5 text-gray-700 group-hover:text-likeColor transition"/>
            </button>
        </div>
    </div>
</template>

<style scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: 2;
}
</style>
